import React, {useCallback} from 'react';
import CallLink from 'components/coaching/CallLink';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import SubmitContentDetailScore from 'components/coaching/SubmitContentDetailScore';
import {List} from 'immutable';
import CoachingPlan, {CoachingReview} from 'records/CoachingPlan';
import User from 'records/User';
import CoachingReviewFileUpload from 'records/CoachingReviewFileUpload';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CoachingContentDetailScore from 'records/CoachingContentDetailScore';

interface ReviewFileUploadProps {
  plan: CoachingPlan;
  reviewFileUpload: CoachingReviewFileUpload;
  call: {id: string; description: string};
  review: CoachingReview;
  isCoach: boolean;
  disabled: boolean;
  addedBy: List<User>;
  otherUser: User;
  setFileUploadScore: (value: CoachingContentDetailScore, feedbackIndex: number) => void;
}

export function ReviewFileUpload(props: ReviewFileUploadProps) {
  const {reviewFileUpload, addedBy, call, review, disabled, plan, otherUser, isCoach} = props;

  // We only support one or two skills per plan
  const [firstSkill, secondSkill] = plan.contentDetails;

  // Skills chosen for this call
  const [firstId, secondId] = reviewFileUpload.contentDetailIds ?? List();
  // If secondId is defined, then both are selected.
  const selectedSkill = secondId ? -1 : firstId;

  const onChange = useCallback(
    (ids: number[]) => {
      CoachingReviewActions.changeFileReviewSkills(review.id, reviewFileUpload.id, ids);
    },
    [review.id, reviewFileUpload.id]
  );

  return (
    <section className="call-for-review">
      <div className="call-details">
        <div className="description">
          <CallLink
            call={call}
            reviewFileUpload={reviewFileUpload}
            reviewId={review.id}
            planId={plan.id}
          />
          <div>
            Added for review by {addedBy && addedBy.map(u => u && u.getDisplayName()).join(', ')} on{' '}
            {reviewFileUpload.addedOn?.format('MM/DD/YYYY')}
          </div>
        </div>
        {disabled && isCoach && secondSkill && (
          <div className="select-skills">
            <p>Skill(s) to review:</p>
            <Select
              fullWidth
              value={selectedSkill}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
              }}
              onChange={e => {
                const id = e.target.value as number;
                if (id === -1) {
                  onChange([firstSkill.id, secondSkill.id]);
                } else {
                  onChange([id]);
                }
              }}>
              <MenuItem value={firstSkill.id}>{firstSkill.skillName}</MenuItem>
              <MenuItem value={secondSkill.id}>{secondSkill.skillName}</MenuItem>
              <MenuItem value={-1}>Both</MenuItem>
            </Select>
          </div>
        )}
      </div>
      <div className="call-score-feedback">
        {reviewFileUpload.reviewFeedbackList?.map((contentDetailScore, j) => (
          <SubmitContentDetailScore
            key={j}
            otherUser={otherUser}
            contentDetailScore={contentDetailScore}
            // only coach can score calls.
            disabled={disabled || !isCoach}
            onChange={val => props.setFileUploadScore(val, j)}
            isCoach={isCoach}
            scorecardId={plan.scorecardId}
          />
        ))}
      </div>
    </section>
  );
}
